import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { WebRTCManager } from '@/lib/webrtc/WebRTCManager';
import { useToast } from '@/hooks/use-toast';

// ────────── Types ──────────

export type CallStatus = 'idle' | 'initiating' | 'ringing' | 'connecting' | 'in-call' | 'ended';

export interface CallParticipant {
  userId: string;
  userName: string;
  audio: boolean;
  video: boolean;
}

export interface IncomingCall {
  callId: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string | null;
  callType: 'audio' | 'video';
  roomId?: string;
  isGroupCall: boolean;
}

export interface CallState {
  callId: string | null;
  status: CallStatus;
  callType: 'audio' | 'video';
  isGroupCall: boolean;
  roomId: string | null;
  callerId: string | null;
  participants: CallParticipant[];
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  callDuration: number;
}

interface CallContextType {
  callState: CallState;
  incomingCall: IncomingCall | null;
  initiateCall: (params: {
    targetUserId?: string;
    targetUserName?: string;
    roomId?: string;
    callType: 'audio' | 'video';
    isGroupCall: boolean;
  }) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
}

const initialCallState: CallState = {
  callId: null,
  status: 'idle',
  callType: 'audio',
  isGroupCall: false,
  roomId: null,
  callerId: null,
  participants: [],
  localStream: null,
  remoteStreams: new Map(),
  isAudioEnabled: true,
  isVideoEnabled: false,
  callDuration: 0,
};

const CallContext = createContext<CallContextType | undefined>(undefined);

// ────────── Provider ──────────

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { socket, currentUser, isConnected } = useChat();
  const { toast } = useToast();

  const [callState, setCallState] = useState<CallState>(initialCallState);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);

  // *** REFS to avoid stale closures — these always hold the latest value ***
  const callStateRef = useRef<CallState>(callState);
  const incomingCallRef = useRef<IncomingCall | null>(incomingCall);
  const socketRef = useRef(socket);
  const isConnectedRef = useRef(isConnected);

  // Keep refs in sync
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);
  useEffect(() => { socketRef.current = socket; }, [socket]);
  useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);

  const webrtcRef = useRef<WebRTCManager | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ringtoneCtxRef = useRef<{ ctx: AudioContext; osc1: OscillatorNode; osc2: OscillatorNode; gain: GainNode; interval: ReturnType<typeof setInterval> } | null>(null);

  // ────────── Helpers (use refs, never stale) ──────────

  const getWebRTC = useCallback(() => {
    if (!webrtcRef.current) {
      webrtcRef.current = new WebRTCManager();
    }
    return webrtcRef.current;
  }, []);

  const emitSocket = useCallback((event: string, data?: any) => {
    if (socketRef.current && isConnectedRef.current) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneCtxRef.current) {
      try {
        clearInterval(ringtoneCtxRef.current.interval);
        ringtoneCtxRef.current.osc1.stop();
        ringtoneCtxRef.current.osc2.stop();
        ringtoneCtxRef.current.ctx.close();
      } catch {
        // Already stopped
      }
      ringtoneCtxRef.current = null;
    }
  }, []);

  const playRingtone = useCallback(async (isOutgoing = false) => {
    stopRingtone();
    try {
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
      }
      // If AudioContext is still suspended (no user gesture yet), skip playing
      if (ctx.state === 'suspended') {
        ctx.close();
        return;
      }
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.value = 0;

      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();

      if (isOutgoing) {
        osc1.frequency.value = 440;
        osc2.frequency.value = 480;
      } else {
        osc1.frequency.value = 523.25;
        osc2.frequency.value = 659.25;
      }

      osc1.type = 'sine';
      osc2.type = 'sine';

      const osc2Gain = ctx.createGain();
      osc2Gain.gain.value = 0.6;

      osc1.connect(gain);
      osc2.connect(osc2Gain);
      osc2Gain.connect(gain);

      osc1.start();
      osc2.start();

      const onDuration = isOutgoing ? 2000 : 1000;
      const offDuration = isOutgoing ? 4000 : 2000;
      const cycleDuration = onDuration + offDuration;
      const startTime = Date.now();

      const interval = setInterval(() => {
        const elapsed = (Date.now() - startTime) % cycleDuration;
        gain.gain.value = elapsed < onDuration ? 0.3 : 0;
      }, 50);

      gain.gain.value = 0.3;

      ringtoneCtxRef.current = { ctx, osc1, osc2, gain, interval };
    } catch {
      // Web Audio API not available
    }
  }, [stopRingtone]);

  const startDurationTimer = useCallback(() => {
    // Only start if not already running — prevents reset when new participants join
    if (durationIntervalRef.current) return;
    durationIntervalRef.current = setInterval(() => {
      setCallState(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  // Full cleanup — also tells the server to force-clean this user's call state
  const cleanupCall = useCallback((notifyServer = true) => {
    stopRingtone();
    stopDurationTimer();

    // Tell the server to clean up, as a safety net
    if (notifyServer) {
      emitSocket('call:cleanup');
    }

    // Stop local media tracks even if WebRTC manager was already nulled
    // This prevents the camera/mic staying on after a failed or cleaned-up call
    const currentState = callStateRef.current;
    if (currentState.localStream) {
      currentState.localStream.getTracks().forEach(track => track.stop());
    }

    webrtcRef.current?.closeAllConnections();
    webrtcRef.current = null;
    setCallState(initialCallState);
    setIncomingCall(null);
  }, [stopRingtone, stopDurationTimer, emitSocket]);

  // ────────── Actions ──────────

  const initiateCall = useCallback(async (params: {
    targetUserId?: string;
    targetUserName?: string;
    roomId?: string;
    callType: 'audio' | 'video';
    isGroupCall: boolean;
  }) => {
    // Read from ref to get latest status (not stale closure)
    if (callStateRef.current.status !== 'idle') {
      toast({ title: 'Already in a call', variant: 'destructive' });
      return;
    }

    const isVideo = params.callType === 'video';
    const webrtc = getWebRTC();

    try {
      const stream = await webrtc.getLocalStream(true, isVideo);

      setCallState({
        ...initialCallState,
        status: 'initiating',
        callType: params.callType,
        isGroupCall: params.isGroupCall,
        roomId: params.roomId || null,
        callerId: currentUser?.id || null,
        localStream: stream,
        isAudioEnabled: true,
        isVideoEnabled: isVideo,
      });

      emitSocket('call:initiate', {
        targetUserId: params.targetUserId,
        roomId: params.roomId,
        callType: params.callType,
        isGroupCall: params.isGroupCall,
      });
    } catch (err) {
      cleanupCall();
      toast({
        title: 'Cannot start call',
        description: 'Please allow microphone/camera access',
        variant: 'destructive',
      });
    }
  }, [getWebRTC, currentUser?.id, emitSocket, cleanupCall, toast]);

  const acceptCall = useCallback(async () => {
    const incoming = incomingCallRef.current;
    if (!incoming) return;

    const isVideo = incoming.callType === 'video';
    const webrtc = getWebRTC();

    try {
      stopRingtone();
      const stream = await webrtc.getLocalStream(true, isVideo);

      setCallState({
        ...initialCallState,
        callId: incoming.callId,
        status: 'connecting',
        callType: incoming.callType,
        isGroupCall: incoming.isGroupCall,
        roomId: incoming.roomId || null,
        callerId: incoming.callerId,
        localStream: stream,
        isAudioEnabled: true,
        isVideoEnabled: isVideo,
      });
      setIncomingCall(null);

      emitSocket('call:accept', {
        callId: incoming.callId,
        userName: currentUser?.full_name || currentUser?.email || 'Unknown',
      });
    } catch (err) {
      cleanupCall();
      toast({
        title: 'Cannot join call',
        description: 'Please allow microphone/camera access',
        variant: 'destructive',
      });
    }
  }, [getWebRTC, stopRingtone, currentUser, emitSocket, cleanupCall, toast]);

  const rejectCall = useCallback(() => {
    const incoming = incomingCallRef.current;
    if (!incoming) return;
    stopRingtone();
    emitSocket('call:reject', { callId: incoming.callId });
    setIncomingCall(null);
  }, [stopRingtone, emitSocket]);

  const endCall = useCallback(() => {
    // Read from ref to always get the latest callId/status
    const current = callStateRef.current;

    if (current.callId) {
      if (current.status === 'ringing' || current.status === 'initiating') {
        // Still ringing — this is a cancel, not an end
        emitSocket('call:cancel', { callId: current.callId });
      } else {
        emitSocket('call:end', { callId: current.callId });
      }
    }

    // Always cleanup locally + tell server to force-clean
    cleanupCall();
  }, [emitSocket, cleanupCall]);

  const toggleAudio = useCallback(() => {
    const current = callStateRef.current;
    const newEnabled = !current.isAudioEnabled;
    webrtcRef.current?.toggleAudio(newEnabled);
    setCallState(prev => ({ ...prev, isAudioEnabled: newEnabled }));
    if (current.callId) {
      emitSocket('call:toggle-media', {
        callId: current.callId,
        audio: newEnabled,
        video: current.isVideoEnabled,
      });
    }
  }, [emitSocket]);

  const toggleVideo = useCallback(() => {
    const current = callStateRef.current;
    const newEnabled = !current.isVideoEnabled;
    webrtcRef.current?.toggleVideo(newEnabled);
    setCallState(prev => ({ ...prev, isVideoEnabled: newEnabled }));
    if (current.callId) {
      emitSocket('call:toggle-media', {
        callId: current.callId,
        audio: current.isAudioEnabled,
        video: newEnabled,
      });
    }
  }, [emitSocket]);

  // ────────── Socket Event Listeners ──────────

  useEffect(() => {
    if (!socket || !isConnected) return;

    // Server confirms call was created — save callId, start outgoing ring tone
    const onCallInitiated = (data: { callId: string }) => {
      setCallState(prev => {
        if (prev.status !== 'initiating') return prev;
        return { ...prev, callId: data.callId, status: 'ringing' };
      });
      playRingtone(true);
    };

    // Incoming call from someone else
    const onCallIncoming = (data: IncomingCall) => {
      // Use ref for latest status — never stale
      const currentStatus = callStateRef.current.status;
      const currentCallId = callStateRef.current.callId;

      // If already in THIS call (e.g. late-joiner re-notification), ignore silently
      if (currentCallId === data.callId) return;

      // If already showing an incoming call notification for THIS call, ignore
      if (incomingCallRef.current?.callId === data.callId) return;

      // If busy in a different call, auto-reject
      if (currentStatus !== 'idle') {
        emitSocket('call:reject', { callId: data.callId });
        return;
      }

      setIncomingCall(data);
      playRingtone(false);
    };

    // Callee accepted our call — stop ringback, start WebRTC handshake
    const onCallAccepted = async (data: { callId: string; acceptedBy: string }) => {
      stopRingtone();
      setCallState(prev => ({ ...prev, status: 'connecting' }));

      const webrtc = getWebRTC();

      webrtc.onIceCandidate = (targetUserId, candidate) => {
        emitSocket('call:ice-candidate', {
          callId: data.callId,
          targetUserId,
          candidate,
        });
      };
      webrtc.onRemoteStream = (userId, stream) => {
        setCallState(prev => {
          const newStreams = new Map(prev.remoteStreams);
          newStreams.set(userId, stream);
          return { ...prev, remoteStreams: newStreams, status: 'in-call' };
        });
        startDurationTimer();
      };
      webrtc.onRemoteStreamRemoved = (userId) => {
        setCallState(prev => {
          const newStreams = new Map(prev.remoteStreams);
          newStreams.delete(userId);
          return { ...prev, remoteStreams: newStreams };
        });
      };

      try {
        const offer = await webrtc.createOffer(data.acceptedBy);
        emitSocket('call:offer', {
          callId: data.callId,
          targetUserId: data.acceptedBy,
          sdp: offer,
        });
      } catch (err) {
        console.error('Failed to create WebRTC offer:', err);
        toast({ title: 'Call connection failed', variant: 'destructive' });
        cleanupCall();
      }
    };

    // Received SDP offer from a peer
    const onCallOffer = async (data: { callId: string; fromUserId: string; sdp: RTCSessionDescriptionInit }) => {
      const webrtc = getWebRTC();

      // Wire up callbacks if not already done
      if (!webrtc.onIceCandidate) {
        webrtc.onIceCandidate = (targetUserId, candidate) => {
          emitSocket('call:ice-candidate', {
            callId: data.callId,
            targetUserId,
            candidate,
          });
        };
        webrtc.onRemoteStream = (userId, stream) => {
          setCallState(prev => {
            const newStreams = new Map(prev.remoteStreams);
            newStreams.set(userId, stream);
            return { ...prev, remoteStreams: newStreams, status: 'in-call' };
          });
          startDurationTimer();
        };
        webrtc.onRemoteStreamRemoved = (userId) => {
          setCallState(prev => {
            const newStreams = new Map(prev.remoteStreams);
            newStreams.delete(userId);
            return { ...prev, remoteStreams: newStreams };
          });
        };
      }

      try {
        const answer = await webrtc.handleOffer(data.fromUserId, data.sdp);
        emitSocket('call:answer', {
          callId: data.callId,
          targetUserId: data.fromUserId,
          sdp: answer,
        });
      } catch (err) {
        console.error('Failed to handle WebRTC offer:', err);
      }
    };

    // Received SDP answer from a peer
    const onCallAnswer = async (data: { callId: string; fromUserId: string; sdp: RTCSessionDescriptionInit }) => {
      try {
        await getWebRTC().handleAnswer(data.fromUserId, data.sdp);
      } catch (err) {
        console.error('Failed to handle WebRTC answer:', err);
      }
    };

    // Received ICE candidate from a peer
    const onIceCandidate = async (data: { callId: string; fromUserId: string; candidate: RTCIceCandidateInit }) => {
      try {
        await getWebRTC().addIceCandidate(data.fromUserId, data.candidate);
      } catch (err) {
        console.error('Failed to add ICE candidate:', err);
      }
    };

    // Call was rejected
    const onCallRejected = () => {
      toast({ title: 'Call rejected' });
      cleanupCall(false); // Server already cleaned up
    };

    // Call ended
    const onCallEnded = (data: { callId: string; endedBy: string; reason: string; duration: number }) => {
      if (data.reason === 'no_answer') {
        toast({ title: 'No answer' });
      }
      cleanupCall(false); // Server already cleaned up
    };

    // Call cancelled (caller hung up before we answered)
    const onCallCancelled = () => {
      toast({ variant: "success", title: 'Call cancelled' });
      cleanupCall(false); // Server already cleaned up
    };

    // Missed call
    const onCallMissed = (data: { callId: string; callerId: string; callerName: string }) => {
      toast({ title: `Missed call from ${data.callerName}` });
      cleanupCall(false); // Server already cleaned up
    };

    // Target user is busy
    const onCallBusy = () => {
      toast({ title: 'User is busy' });
      cleanupCall(false);
    };

    // Call error — always notify server to force-clean stale state
    const onCallError = (data: { message: string }) => {
      toast({ title: 'Call error', description: data.message, variant: 'destructive' });
      cleanupCall(true);
    };

    // Group call: new participant joined
    const onParticipantJoined = async (data: { callId: string; userId: string; userName: string }) => {
      setCallState(prev => ({
        ...prev,
        participants: [...prev.participants.filter(p => p.userId !== data.userId), {
          userId: data.userId,
          userName: data.userName,
          audio: true,
          video: prev.callType === 'video',
        }],
      }));

      const webrtc = getWebRTC();
      try {
        const offer = await webrtc.createOffer(data.userId);
        emitSocket('call:offer', {
          callId: data.callId,
          targetUserId: data.userId,
          sdp: offer,
        });
      } catch (err) {
        console.error(`Failed to create offer for new participant ${data.userId}:`, err);
      }
    };

    // Group call: participant left — keep call alive for remaining members
    const onParticipantLeft = (data: { callId: string; userId: string }) => {
      webrtcRef.current?.removePeer(data.userId);
      setCallState(prev => {
        const newParticipants = prev.participants.filter(p => p.userId !== data.userId);
        const newStreams = new Map(prev.remoteStreams);
        newStreams.delete(data.userId);

        // If no remote streams left, we're the last one — end the call
        if (newStreams.size === 0 && prev.status === 'in-call') {
          // Schedule cleanup after state update (avoid setState during setState)
          setTimeout(() => {
            toast({ title: 'Everyone else left the call' });
            cleanupCall(true);
          }, 0);
        }

        return { ...prev, participants: newParticipants, remoteStreams: newStreams };
      });
    };

    // Media state change from a participant
    const onMediaState = (data: { callId: string; userId: string; audio: boolean; video: boolean }) => {
      setCallState(prev => ({
        ...prev,
        participants: prev.participants.map(p =>
          p.userId === data.userId ? { ...p, audio: data.audio, video: data.video } : p
        ),
      }));
    };

    // On (re)connect: if client is idle, force-clean any stale server-side call state
    // This handles the case where a previous socket disconnected mid-call
    if (callStateRef.current.status === 'idle') {
      socket.emit('call:cleanup');
    }

    // Register all listeners
    socket.on('call:initiated', onCallInitiated);
    socket.on('call:incoming', onCallIncoming);
    socket.on('call:accepted', onCallAccepted);
    socket.on('call:offer', onCallOffer);
    socket.on('call:answer', onCallAnswer);
    socket.on('call:ice-candidate', onIceCandidate);
    socket.on('call:rejected', onCallRejected);
    socket.on('call:ended', onCallEnded);
    socket.on('call:cancelled', onCallCancelled);
    socket.on('call:missed', onCallMissed);
    socket.on('call:busy', onCallBusy);
    socket.on('call:error', onCallError);
    socket.on('call:participant-joined', onParticipantJoined);
    socket.on('call:participant-left', onParticipantLeft);
    socket.on('call:media-state', onMediaState);

    return () => {
      socket.off('call:initiated', onCallInitiated);
      socket.off('call:incoming', onCallIncoming);
      socket.off('call:accepted', onCallAccepted);
      socket.off('call:offer', onCallOffer);
      socket.off('call:answer', onCallAnswer);
      socket.off('call:ice-candidate', onIceCandidate);
      socket.off('call:rejected', onCallRejected);
      socket.off('call:ended', onCallEnded);
      socket.off('call:cancelled', onCallCancelled);
      socket.off('call:missed', onCallMissed);
      socket.off('call:busy', onCallBusy);
      socket.off('call:error', onCallError);
      socket.off('call:participant-joined', onParticipantJoined);
      socket.off('call:participant-left', onParticipantLeft);
      socket.off('call:media-state', onMediaState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
      stopDurationTimer();
      webrtcRef.current?.closeAllConnections();
      webrtcRef.current = null;
    };
  }, [stopRingtone, stopDurationTimer]);

  const value: CallContextType = {
    callState,
    incomingCall,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return ctx;
}
